// SEQ-192: keeps data-seq-private on every dollar figure currently on screen.
// The rules are in seqReportPrivacy.ts; this file is only the DOM walking.
//
// Runs while a signed-in user has the report widget loaded. It marks the
// element that holds the text, so the widget blurs that element's box in the
// screenshot. Anything it marks by mistake is only blurred, never hidden.

/// <reference lib="dom" />
import {
  inputLooksLikeMoney,
  SEQ_PRIVATE_ATTRIBUTE,
  textLooksLikeMoney,
} from "./seqReportPrivacy.ts";

// The slice of the widget API the sweep touches. open() and the
// seqreport:open event are feature-detected: the widget is third party.
interface WidgetApi {
  open?: (...args: unknown[]) => unknown;
}
type WidgetWindow = { SEQReport?: WidgetApi };

// A shake opens the report on phones. devicemotion fires about 60 times a
// second, so a shake re-sweeps at most once per second.
const SHAKE_THRESHOLD = 15;
const SHAKE_SWEEP_GAP_MS = 1000;

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "SEQ-REPORT"]);

function directText(el: Element): string {
  let out = "";
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) out += n.nodeValue ?? "";
  });
  return out;
}

function mark(el: Element): void {
  if (!el.hasAttribute(SEQ_PRIVATE_ATTRIBUTE)) el.setAttribute(SEQ_PRIVATE_ATTRIBUTE, "");
}

function markControl(el: Element): void {
  // A textarea keeps its text in .value once React has written it.
  if (el instanceof HTMLTextAreaElement) {
    if (textLooksLikeMoney(el.value)) mark(el);
    return;
  }
  if (el instanceof HTMLInputElement) {
    if (el.type === "checkbox" || el.type === "radio" || el.type === "hidden") return;
    if (inputLooksLikeMoney({ type: el.type, inputMode: el.inputMode, value: el.value })) mark(el);
  }
}

function sweepElement(el: Element): void {
  if (SKIP_TAGS.has(el.tagName.toUpperCase())) return;
  if (el.hasAttribute(SEQ_PRIVATE_ATTRIBUTE)) return;
  markControl(el);
  if (textLooksLikeMoney(directText(el))) mark(el);
}

function insideSkipped(el: Element | null): boolean {
  for (let n: Element | null = el; n; n = n.parentElement) {
    if (SKIP_TAGS.has(n.tagName.toUpperCase())) return true;
  }
  return false;
}

// Depth first, so a skipped element takes its whole subtree with it (the
// widget's own light DOM must never be marked, or its form would blur).
function walk(el: Element): void {
  if (SKIP_TAGS.has(el.tagName.toUpperCase())) return;
  sweepElement(el);
  el.childNodes.forEach((c) => {
    if (c instanceof Element) walk(c);
  });
}

// Sweeps a subtree (including the root). Cheap enough to run on every
// mutation batch: it only reads text and sets one attribute.
export function sweepForMoney(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) {
    if (root.parentElement && !insideSkipped(root.parentElement)) sweepElement(root.parentElement);
    return;
  }
  if (!(root instanceof Element) || insideSkipped(root)) return;
  walk(root);
}

// Starts sweeping `root` now and on every change beneath it. Returns a stop
// function that disconnects the observer.
export function startMoneySweep(root: Node = document.body): () => void {
  sweepForMoney(root);

  const pending = new Set<Node>();
  let scheduled = false;
  const flush = () => {
    scheduled = false;
    pending.forEach((n) => {
      if (n.isConnected) sweepForMoney(n);
    });
    pending.clear();
  };
  const queue = (n: Node) => {
    pending.add(n);
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  };

  const observer = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "characterData") queue(r.target);
      else r.addedNodes.forEach(queue);
    }
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true });

  // Typing a figure into a text field changes a property, not the DOM.
  const onInput = (e: Event) => {
    if (e.target instanceof Element && !insideSkipped(e.target)) markControl(e.target);
  };
  document.addEventListener("input", onInput, true);

  // Every way of opening the report must re-sweep the whole page first:
  // figures written since the last frame and values set on inputs without a DOM
  // change would otherwise reach the screenshot unmarked. The paths are the
  // widget's button (window capture runs before the widget's own listeners),
  // its open event, SEQReport.open() called from code, and a phone shake.
  const resweep = () => sweepForMoney(document.body);

  const inWidget = (e: Event) => {
    const path = typeof e.composedPath === "function" ? e.composedPath() : [];
    return path.some((n) => n instanceof Element && n.tagName === "SEQ-REPORT");
  };

  // Also, a click on a container that holds a marked figure would log the
  // container's text into the report's click log. Mark that container for the
  // length of the event only, so the widget records "[private]" instead.
  const onClick = (e: MouseEvent) => {
    if (inWidget(e)) {
      resweep();
      return;
    }
    const t = e.target;
    if (
      t instanceof Element &&
      !t.closest(`[${SEQ_PRIVATE_ATTRIBUTE}]`) &&
      t.querySelector(`[${SEQ_PRIVATE_ATTRIBUTE}]`)
    ) {
      t.setAttribute(SEQ_PRIVATE_ATTRIBUTE, "");
      setTimeout(() => t.removeAttribute(SEQ_PRIVATE_ATTRIBUTE), 0);
    }
  };

  let lastShakeSweep = 0;
  const onMotion = (e: Event) => {
    const a = (e as DeviceMotionEvent).accelerationIncludingGravity;
    if (!a) return;
    const force = Math.max(Math.abs(a.x ?? 0), Math.abs(a.y ?? 0), Math.abs(a.z ?? 0));
    const now = Date.now();
    if (force >= SHAKE_THRESHOLD && now - lastShakeSweep >= SHAKE_SWEEP_GAP_MS) {
      lastShakeSweep = now;
      resweep();
    }
  };

  // SEQReport.open(): sweep, then open. Wrapped once the API exists, and
  // again if the widget replaces its API object after a reset.
  let unwrapOpen: (() => void) | null = null;
  const wrapOpen = () => {
    const api = (window as unknown as WidgetWindow).SEQReport;
    const original = api?.open;
    if (!api || typeof original !== "function") return;
    if ((original as { __seqSwept?: boolean }).__seqSwept) return;
    const wrapped = function (this: unknown, ...args: unknown[]) {
      resweep();
      return original.apply(this ?? api, args);
    };
    (wrapped as { __seqSwept?: boolean }).__seqSwept = true;
    try {
      api.open = wrapped;
    } catch {
      // A locked down widget API cannot be wrapped. The click, event and
      // shake paths still sweep, and sign out must never be blocked by this.
      return;
    }
    unwrapOpen = () => {
      try {
        if (api.open === wrapped) api.open = original;
      } catch {
        // Nothing to restore.
      }
    };
  };
  wrapOpen();

  const onWidgetReady = () => wrapOpen();
  window.addEventListener("click", onClick, true);
  window.addEventListener("seqreport:open", resweep, true);
  window.addEventListener("seqreport:ready", onWidgetReady);
  window.addEventListener("devicemotion", onMotion);

  return () => {
    observer.disconnect();
    unwrapOpen?.();
    window.removeEventListener("click", onClick, true);
    window.removeEventListener("seqreport:open", resweep, true);
    window.removeEventListener("seqreport:ready", onWidgetReady);
    window.removeEventListener("devicemotion", onMotion);
    document.removeEventListener("input", onInput, true);
    pending.clear();
  };
}
